<template>
  <div>
    <transition name="modal">
      <delete-modal
        v-if="showDeleteModal"
        :loading="loadingDelete"
        @close="closeDeleteModal()"
        @confirmDelete="deleteElement"
      ></delete-modal>
    </transition>

    <div v-if="!loading">
      <div v-if="coupon" class="flex flex-col">
        <div class="flex items-center space-x-2">
          <router-link :to="{ name: 'couponsIndex' }" class="system-button">
            <arrow-icon class="w-4 rotate-90"></arrow-icon>
          </router-link>
        </div>

        <div class="card mt-2">
          <div class="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h2 class="font-mono">{{ coupon.code }}</h2>
              <p class="text-content-400">{{ coupon.name }}</p>
            </div>
            <span
              :class="[
                'p-1 text-xs rounded text-white h-fit',
                coupon.is_active ? 'bg-success-500' : 'bg-content-300',
              ]"
              >{{ coupon.is_active ? "Activo" : "Inactivo" }}</span
            >
          </div>

          <div class="div-line"></div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p class="font-medium">Descripción</p>
              <p>{{ coupon.description || "—" }}</p>
            </div>
            <div>
              <p class="font-medium">Aplica a</p>
              <p>{{ appliesToLabel(coupon.applies_to) }}</p>
            </div>
            <div>
              <p class="font-medium">Tipo de descuento</p>
              <p>
                {{ coupon.discount_type === "percentage" ? "Porcentaje" : "Monto fijo" }}
                —
                {{
                  coupon.discount_type === "percentage"
                    ? `${coupon.discount_value}%`
                    : integerToCurrency(coupon.discount_value)
                }}
              </p>
            </div>
            <div>
              <p class="font-medium">Usos</p>
              <p>
                {{ coupon.uses_count }}{{ coupon.max_uses ? ` / ${coupon.max_uses}` : " (sin límite total)" }}
              </p>
            </div>
            <div>
              <p class="font-medium">Usos por cliente</p>
              <p>{{ coupon.max_uses_per_client }}</p>
            </div>
            <div>
              <p class="font-medium">Usos por email</p>
              <p>{{ coupon.max_uses_per_email }}</p>
            </div>
            <div>
              <p class="font-medium">Vigente desde</p>
              <p>{{ coupon.valid_from ? formatDatetime(coupon.valid_from) : "—" }}</p>
            </div>
            <div>
              <p class="font-medium">Vigente hasta</p>
              <p>{{ coupon.valid_until ? formatDatetime(coupon.valid_until) : "—" }}</p>
            </div>
          </div>

          <div class="flex space-x-4 items-center mt-4">
            <router-link
              v-if="hasPermission('edit_coupons')"
              :to="{ name: 'couponsEditForm', params: { id: coupon.id } }"
              ><edit-button
            /></router-link>

            <delete-button
              v-if="hasPermission('delete_coupons')"
              class="delete-icon-button"
              @click.prevent="openDeleteModal()"
            />
          </div>
        </div>

        <div class="card mt-4">
          <h3 class="font-medium">Historial de canjes</h3>

          <table v-if="redemptions.length" class="table-auto text-sm mt-2 bg-white w-full">
            <thead class="text-left">
              <tr class="bg-primary-100">
                <th class="px-4 py-3 rounded-l-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Fecha</th>
                <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Tipo</th>
                <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Cliente / Email</th>
                <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Origen</th>
                <th class="px-4 py-3 rounded-r-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Descuento</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in redemptions" :key="r.id" class="table-row">
                <td class="table-cell">{{ formatDatetime(r.created_at) }}</td>
                <td class="table-cell">{{ redeemableLabel(r.redeemable_type) }}</td>
                <td class="table-cell">
                  <span v-if="r.client">
                    {{ r.client.name }} {{ r.client.last_name }}
                    <span v-if="r.client.user" class="text-content-300">
                      ({{ r.client.user.email }})
                    </span>
                  </span>
                  <span v-else>{{ r.guest_email }}</span>
                </td>
                <td class="table-cell">#{{ r.redeemable_id }}</td>
                <td class="table-cell">{{ integerToCurrency(r.discount_amount) }}</td>
              </tr>
            </tbody>
          </table>

          <p v-else class="text-content-300 mt-2">
            Aún no hay canjes registrados.
          </p>

          <pagination
            v-if="totalPages > 1"
            :current-page="currentPage"
            :total-pages="totalPages"
            @page-change="onPageChange"
          />
        </div>
      </div>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import deleteMixin from "@/mixins/deleteMixin";
import Pagination from "@/components/ui/Pagination.vue";
import { formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "Coupon Detail",

  components: { Pagination },

  data() {
    return {
      coupon: null,
      redemptions: [],
      loading: false,
      loadingDelete: false,
      currentPage: 1,
      totalPages: 0,
    };
  },

  mixins: [deleteMixin],

  created() {
    this.$store.commit("setStoreSelectedTab", "coupons");
    this.loadCoupon();
    this.loadRedemptions();
  },

  methods: {
    formatDatetime,
    integerToCurrency,

    appliesToLabel(value) {
      switch (value) {
        case "products":
          return "Productos";
        case "plans":
          return "Planes";
        default:
          return "Todo";
      }
    },

    redeemableLabel(type) {
      if (!type) return "—";
      if (type.endsWith("ShopOrder")) return "Tienda";
      if (type.endsWith("Quote")) return "Plan";
      return type.split("\\").pop();
    },

    async loadCoupon() {
      this.loading = true;

      try {
        this.coupon = await api.coupons.getById(this.$route.params.id);
        this.$store.commit("setBreadcrumbLabel", {
          routeName: "couponsDetail",
          label: this.coupon.code,
        });
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
        this.$router.push({ name: "couponsIndex" });
      }

      this.loading = false;
    },

    async loadRedemptions() {
      try {
        const response = await api.coupons.getRedemptions(this.$route.params.id, {
          page: this.currentPage,
        });
        this.redemptions = response.data || [];
        this.currentPage = response.current_page || 1;
        this.totalPages = response.last_page || 0;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadRedemptions();
    },

    async deleteElement() {
      this.loadingDelete = true;

      try {
        const response = await api.coupons.delete(this.coupon.id);
        this.$store.dispatch("successSnack", response.message);
        this.closeDeleteModal();
        this.$router.push({ name: "couponsIndex" });
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingDelete = false;
    },
  },
};
</script>
